import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Box, MenuItem } from '@material-ui/core';
import { i18n } from '@lingui/core';
import { useFormContext } from 'react-hook-form';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import {
    AGENT_WEB_PROXY_ADDRESS,
    AGENT_WEB_PROXY_AUTO_PROXY_OPTION,
    AGENT_WEB_PROXY_AUTOCONFIG_URL,
    AGENT_WEB_PROXY_DEFAULT_PROXY_OPTION,
    AGENT_WEB_PROXY_ENABLED,
    AGENT_WEB_PROXY_ENCRYPTED_PASSWORD_VALUE,
    AGENT_WEB_PROXY_ENCRYPTED_USERNAME_VALUE,
    AGENT_WEB_PROXY_MANUAL_PROXY_OPTION,
    AGENT_WEB_PROXY_OPTIONS,
    AGENT_WEB_PROXY_OVERRIDDEN,
    AGENT_WEB_PROXY_PORT,
    AGENT_WEB_PROXY_SELECTION_FIELD,
} from './AgentConstants';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import NgpNumberField from '../../form/NgpNumberField';
import NgpPasswordField from '../../form/NgpPasswordField';
import {
    WEB_PROXY_AUTO_URL,
    WEB_PROXY_MANUAL_PORT,
    WEB_PROXY_MANUAL_SERVER_ADDRESS,
    WEB_PROXY_NO_PROXY_OPTION,
    WEB_PROXY_SELECTION_FIELD,
    WEB_PROXY_USERNAME,
} from '../agent-settings/AgentSettingsConstants';

const webProxyAddressLabel = i18n._(t`Proxy Server Address`);
const webProxyUrlLabel = i18n._(t`Proxy Auto-Config URL`);
const webProxyPortLabel = i18n._(t`Port`);
const webProxyUserNameLabel = i18n._(t`Username`);
const overriddenSubtext = i18n._(t`Web proxy is overridden on the nPoint`);
const noGlobalProxyText = i18n._(t`A Default Web Proxy has not been configured (nPoint Settings)`);

export function AgentDetailWebProxy({ formData }) {
    const { watch } = useFormContext();
    const webProxyOverridden = watch(AGENT_WEB_PROXY_OVERRIDDEN);
    const webProxyEnabled = watch(AGENT_WEB_PROXY_ENABLED);
    const webProxySelection = watch(AGENT_WEB_PROXY_SELECTION_FIELD);

    const isGlobalProxyConfigured = _.get(formData, WEB_PROXY_SELECTION_FIELD) !== WEB_PROXY_NO_PROXY_OPTION;

    return (
        <>
            {webProxyOverridden && (
                <FormSection title={<Trans>Web Proxy</Trans>} subText={overriddenSubtext}>
                    <NgpLabelValue
                        dataLabel={webProxyAddressLabel}
                        dataValue={_.get(formData, AGENT_WEB_PROXY_ADDRESS)}
                    />
                    <NgpLabelValue
                        dataLabel={webProxyAddressLabel}
                        dataValue={_.get(formData, AGENT_WEB_PROXY_AUTOCONFIG_URL)}
                    />
                    <NgpLabelValue dataLabel={webProxyPortLabel} dataValue={_.get(formData, AGENT_WEB_PROXY_PORT)} />
                </FormSection>
            )}

            {!webProxyOverridden && (
                <FormSection title={<Trans>Web Proxy</Trans>}>
                    <NgpToggle fieldName={AGENT_WEB_PROXY_ENABLED} label={<Trans>Configure Web Proxy</Trans>} />

                    {webProxyEnabled && (
                        <>
                            <NgpSelect fieldName={AGENT_WEB_PROXY_SELECTION_FIELD} required>
                                {_.map(AGENT_WEB_PROXY_OPTIONS, (value, key) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </NgpSelect>

                            {webProxySelection === AGENT_WEB_PROXY_DEFAULT_PROXY_OPTION && (
                                <Box mt={3}>
                                    {!isGlobalProxyConfigured && noGlobalProxyText}
                                    {isGlobalProxyConfigured && (
                                        <>
                                            <NgpLabelValue
                                                dataLabel={webProxyAddressLabel}
                                                dataValue={_.get(formData, WEB_PROXY_MANUAL_SERVER_ADDRESS)}
                                            />
                                            <NgpLabelValue
                                                dataLabel={webProxyPortLabel}
                                                dataValue={_.get(formData, WEB_PROXY_MANUAL_PORT)}
                                            />
                                            <NgpLabelValue
                                                dataLabel={webProxyUrlLabel}
                                                dataValue={_.get(formData, WEB_PROXY_AUTO_URL)}
                                            />
                                            <NgpLabelValue
                                                dataLabel={webProxyUserNameLabel}
                                                dataValue={_.get(formData, WEB_PROXY_USERNAME)}
                                            />
                                        </>
                                    )}
                                </Box>
                            )}

                            {webProxySelection === AGENT_WEB_PROXY_MANUAL_PROXY_OPTION && (
                                <>
                                    <NgpTextField
                                        fieldName={AGENT_WEB_PROXY_ADDRESS}
                                        label={<Trans>Proxy Server Address</Trans>}
                                        required
                                        inputProps={{
                                            size: 65,
                                            maxLength: 2083,
                                            autoComplete: AUTO_COMPLETE_DEFAULT,
                                        }}
                                    />

                                    <NgpNumberField
                                        fieldName={AGENT_WEB_PROXY_PORT}
                                        label={<Trans>Port</Trans>}
                                        min="1"
                                        max="65535"
                                        inputProps={{
                                            size: 5,
                                            maxLength: 5,
                                        }}
                                    />

                                    <NgpTextField
                                        fieldName={AGENT_WEB_PROXY_ENCRYPTED_USERNAME_VALUE}
                                        label={<Trans>Username</Trans>}
                                        inputProps={{
                                            size: 65,
                                            maxLength: 2083,
                                            autoComplete: AUTO_COMPLETE_DEFAULT,
                                        }}
                                    />

                                    <NgpPasswordField
                                        fieldName={AGENT_WEB_PROXY_ENCRYPTED_PASSWORD_VALUE}
                                        label={<Trans>Password</Trans>}
                                        inputProps={{ size: 65, maxLength: 2083 }}
                                        togglePasswordVisibility
                                    />
                                </>
                            )}

                            {webProxySelection === AGENT_WEB_PROXY_AUTO_PROXY_OPTION && (
                                <>
                                    <NgpTextField
                                        fieldName={AGENT_WEB_PROXY_AUTOCONFIG_URL}
                                        label={<Trans>Proxy Auto-Config URL</Trans>}
                                        required
                                        inputProps={{
                                            size: 65,
                                            maxLength: 2083,
                                            autoComplete: AUTO_COMPLETE_DEFAULT,
                                        }}
                                    />

                                    <NgpTextField
                                        fieldName={AGENT_WEB_PROXY_ENCRYPTED_USERNAME_VALUE}
                                        label={<Trans>Username</Trans>}
                                        inputProps={{
                                            size: 65,
                                            maxLength: 2083,
                                            autoComplete: AUTO_COMPLETE_DEFAULT,
                                        }}
                                    />

                                    <NgpPasswordField
                                        fieldName={AGENT_WEB_PROXY_ENCRYPTED_PASSWORD_VALUE}
                                        label={<Trans>Password</Trans>}
                                        inputProps={{ size: 65, maxLength: 2083 }}
                                        togglePasswordVisibility
                                    />
                                </>
                            )}
                        </>
                    )}
                </FormSection>
            )}
        </>
    );
}
